import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Repeat, Plus, Play, Trash2, Zap, Users, CalendarDays, Pencil } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const departments = ["GST", "Income Tax", "ROC", "Accounting", "Audit", "TDS", "Litigation"];
const priorities = ["Critical", "High", "Medium", "Low"];
const frequencies = ["Monthly", "Quarterly", "Yearly"];
const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const freqColor = {
  Monthly: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  Quarterly: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  Yearly: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
};

const defaultForm = {
  title_template: "GSTR-3B for {client} — {period}",
  description: "Monthly GSTR-3B filing",
  department: "GST",
  priority: "High",
  frequency: "Monthly",
  due_day: 20,
  due_month: 3,
  client_ids: [],
  assigned_to: "",
  active: true,
};

export default function Recurring() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const canManage = me?.role === "Owner" || me?.role === "Partner";

  function openNew() {
    setEditingId(null);
    setForm(defaultForm);
    setOpen(true);
  }

  function openEdit(t) {
    setEditingId(t.id);
    setForm({
      title_template: t.title_template || "",
      description: t.description || "",
      department: t.department || "GST",
      priority: t.priority || "High",
      frequency: t.frequency || "Monthly",
      due_day: t.due_day ?? 20,
      due_month: t.due_month ?? 3,
      client_ids: t.client_ids || [],
      assigned_to: t.assigned_to || "",
      active: t.active !== false,
    });
    setOpen(true);
  }

  async function load() {
    const { data } = await api.get("/recurring");
    setRows(data);
  }
  useEffect(() => {
    load();
    api.get("/users").then(({ data }) => setUsers(data));
    api.get("/clients").then(({ data }) => setClients(data));
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (form.client_ids.length === 0) {
      toast.error("Select at least one client");
      return;
    }
    try {
      const payload = { ...form };
      if (!payload.assigned_to) delete payload.assigned_to;
      if (editingId) {
        await api.patch(`/recurring/${editingId}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/recurring", payload);
        toast.success(`Template created for ${form.client_ids.length} clients`);
      }
      setOpen(false);
      setEditingId(null);
      setForm(defaultForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  async function generateOne(id) {
    const { data } = await api.post(`/recurring/${id}/generate`);
    if (data.created === 0) {
      toast.info("No new tasks — already generated for this period");
    } else {
      toast.success(`Created ${data.created} tasks for current period`);
    }
    load();
  }

  async function runAll() {
    const { data } = await api.post("/recurring/run-all");
    toast.success(`${data.created} tasks created across ${data.templates} templates`);
    load();
  }

  async function del(id) {
    if (!confirm("Delete this recurring template? Existing tasks stay.")) return;
    await api.delete(`/recurring/${id}`);
    toast.success("Deleted");
    load();
  }

  function toggleClient(cid) {
    setForm(f => ({
      ...f,
      client_ids: f.client_ids.includes(cid) ? f.client_ids.filter(x => x !== cid) : [...f.client_ids, cid],
    }));
  }
  function selectAllClients() {
    setForm(f => ({ ...f, client_ids: clients.map(c => c.id) }));
  }
  function clearClients() { setForm(f => ({ ...f, client_ids: [] })); }

  const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));

  function scheduleLabel(t) {
    if (t.frequency === "Monthly") return `Day ${t.due_day} of every month`;
    if (t.frequency === "Quarterly") return `Day ${t.due_day} of last month in each quarter`;
    return `${months[(t.due_month || 3) - 1]} ${t.due_day} yearly`;
  }

  return (
    <div className="space-y-5" data-testid="recurring-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Repeat className="w-3 h-3" /> Task automation
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Recurring Tasks</h1>
          <p className="text-sm text-muted-foreground mt-1">Auto-create GST, TDS, ROC and other periodic filings across multiple clients in bulk.</p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button data-testid="run-all-btn" variant="outline" onClick={runAll}><Zap className="w-4 h-4 mr-1.5" />Run all now</Button>
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(defaultForm); } }}>
              <DialogTrigger asChild>
                <Button data-testid="add-recurring-btn" className="bg-blue-600 hover:bg-blue-700" onClick={openNew}><Plus className="w-4 h-4 mr-1.5" />New Template</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editingId ? "Edit recurring template" : "New recurring task template"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label>Task title template *</Label>
                    <Input data-testid="rec-title-input" required value={form.title_template} onChange={e => setForm({ ...form, title_template: e.target.value })} />
                    <div className="text-[10px] text-muted-foreground mt-1">
                      Use <span className="font-mono">{"{client}"}</span> and <span className="font-mono">{"{period}"}</span> as placeholders.
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Department</Label>
                      <Select value={form.department} onValueChange={v => setForm({ ...form, department: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Priority</Label>
                      <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Assign To</Label>
                      <Select value={form.assigned_to} onValueChange={v => setForm({ ...form, assigned_to: v })}>
                        <SelectTrigger data-testid="rec-assign-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                        <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Frequency</Label>
                      <Select value={form.frequency} onValueChange={v => setForm({ ...form, frequency: v })}>
                        <SelectTrigger data-testid="rec-freq-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{frequencies.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Due Day (1–31)</Label>
                      <Input type="number" min={1} max={31} value={form.due_day} onChange={e => setForm({ ...form, due_day: parseInt(e.target.value || 1) })} />
                    </div>
                    {form.frequency === "Yearly" && (
                      <div><Label>Due Month</Label>
                        <Select value={String(form.due_month)} onValueChange={v => setForm({ ...form, due_month: parseInt(v) })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{months.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Apply to clients * ({form.client_ids.length} selected)</Label>
                      <div className="flex gap-3 text-[11px]">
                        <button type="button" onClick={selectAllClients} className="text-blue-600 hover:underline">Select all</button>
                        <button type="button" onClick={clearClients} className="text-muted-foreground hover:underline">Clear</button>
                      </div>
                    </div>
                    <div className="border border-border rounded-md max-h-52 overflow-y-auto divide-y divide-border">
                      {clients.length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">No clients.</div>}
                      {clients.map(c => (
                        <label key={c.id} data-testid={`rec-client-toggle-${c.id}`} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/40 cursor-pointer">
                          <Checkbox checked={form.client_ids.includes(c.id)} onCheckedChange={() => toggleClient(c.id)} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">{c.client_name}</div>
                            <div className="text-[10px] text-muted-foreground font-mono">{c.gstin || c.pan || c.client_code}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <DialogFooter><Button data-testid="save-recurring-btn" type="submit" className="bg-blue-600 hover:bg-blue-700">{editingId ? "Save changes" : "Create template"}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <Card className="p-0">
        {rows.length === 0 && (
          <div className="p-12 text-center">
            <Repeat className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
            <div className="text-sm font-medium">No recurring templates yet</div>
            <div className="text-xs text-muted-foreground mt-1">Create one for GSTR-3B, TDS, MGT-7 etc. to auto-generate tasks every period.</div>
          </div>
        )}
        <div className="divide-y divide-border">
          {rows.map(t => (
            <div key={t.id} className="p-5" data-testid={`recurring-row-${t.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-md bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-900 grid place-items-center">
                  <Repeat className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`${freqColor[t.frequency]} border-0 text-[10px]`}>{t.frequency}</Badge>
                    <Badge variant="outline" className="text-[10px] font-mono">{t.department}</Badge>
                    {!t.active && <Badge variant="outline" className="text-[10px] text-muted-foreground">Paused</Badge>}
                  </div>
                  <div className="font-semibold text-sm">{t.title_template}</div>
                  <div className="text-[11px] text-muted-foreground mt-1 flex items-center flex-wrap gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {scheduleLabel(t)}</span>
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {t.client_ids?.length || 0} clients</span>
                    <span>· Assignee: {t.assigned_to ? userMap[t.assigned_to] || "—" : "—"}</span>
                    {t.last_generated_period && (
                      <span className="font-mono">· Last run: {t.last_generated_period}
                        {t.last_generated_at ? ` (${format(parseISO(t.last_generated_at), "dd MMM HH:mm")})` : ""}</span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <div className="flex items-center gap-1">
                    <Button data-testid={`generate-${t.id}`} size="sm" variant="outline" onClick={() => generateOne(t.id)} className="h-8">
                      <Play className="w-3 h-3 mr-1" /> Generate now
                    </Button>
                    <Button
                      data-testid={`edit-recurring-${t.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(t)}
                      className="h-8"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {me?.role === "Owner" && (
                      <Button
                        data-testid={`delete-recurring-${t.id}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => del(t.id)}
                        className="h-8 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
